import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, Outlet } from "react-router-dom";
import { UploadCloud, Loader2, TriangleAlert } from "lucide-react";
import { Button } from "@cms/ui/components/button";
import { Badge } from "@cms/ui/components/badge";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@cms/ui/components/alert-dialog";
import { Sidebar } from "./sidebar";
import { Breadcrumbs, type Crumb } from "@cms/ui/components/breadcrumbs";
import type { ApiClient } from "../lib/api";
import { deriveBuildState } from "../lib/build";
import type { Impersonation, PortalUser, TenantStatus } from "../lib/outlet-context";

interface PortalShellProps {
  user: PortalUser | null;
  /** Authenticated API client; null when no API is configured. */
  api: ApiClient | null;
  tenantId: string;
  environment: string | null;
  impersonation: Impersonation | null;
  onSignOut: () => void;
  changePassword: (password: string) => Promise<string | null>;
}

export function PortalShell({ user, api, tenantId, environment, impersonation, onSignOut, changePassword }: PortalShellProps) {
  const [status, setStatus] = useState<TenantStatus | null>(null);
  const [statusError, setStatusError] = useState("");
  // Pages that drill in (e.g. the per-menu editor) publish a breadcrumb trail
  // into the header via setBreadcrumbs; top-level pages leave it null.
  const [breadcrumbs, setBreadcrumbs] = useState<Crumb[] | null>(null);

  const refreshStatus = useCallback(() => {
    if (!api) return;
    api.request<TenantStatus>("/tenant/status")
      .then((data) => {
        setStatus(data);
        setStatusError("");
      })
      .catch((err: unknown) => {
        setStatus(null);
        setStatusError(err instanceof Error ? err.message : "Failed to load status");
      });
  }, [api]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // While a build is running, poll the lightweight build-status endpoint so the
  // dashboard updates itself; on completion do one full refresh for timestamps
  // and storage. Single-flight, with a hard stop so it never polls forever.
  const buildPollingRef = useRef(false);
  useEffect(() => {
    if (!api || !status?.build_in_progress) return;

    let cancelled = false;
    const deadline = Date.now() + 5 * 60 * 1000;
    const interval = setInterval(async () => {
      if (buildPollingRef.current) return;
      if (Date.now() > deadline) {
        clearInterval(interval);
        return;
      }
      buildPollingRef.current = true;
      try {
        const data = await api.request<TenantStatus>("/tenant/build-status");
        if (cancelled) return;
        if (data.build_in_progress) {
          setStatus((prev) => (prev ? { ...prev, ...data } : prev));
        } else {
          clearInterval(interval);
          refreshStatus();
        }
      } catch {
        // Transient failure — keep polling until the deadline.
      } finally {
        buildPollingRef.current = false;
      }
    }, 6000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [api, status?.build_in_progress, refreshStatus]);

  const activeModules = status?.active_modules ?? [];

  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState("");

  // The single site build trigger: every page/settings/design save is staged,
  // so the live site only rebuilds when the client clicks Publish here.
  const publish = async () => {
    if (!api) return;
    setPublishing(true);
    setPublishError("");
    try {
      await api.request("/tenant/publish", { method: "POST" });
    } catch (err) {
      // A failed dispatch returns a 502 the API client throws on. The server has
      // already recorded the failed build, so the refresh below reflects it.
      setPublishError(err instanceof Error ? err.message : "Publish failed");
    } finally {
      // Refresh on both paths: success surfaces Building… immediately, failure
      // surfaces the Build failed state rather than a silent no-op.
      refreshStatus();
      setPublishing(false);
    }
  };

  const buildState = deriveBuildState(status);
  const buildFailed = buildState.state === "failed";
  // Allow publishing when there are staged changes OR the last build failed, so a
  // failed build can be retried even without further edits.
  const canPublish = Boolean(status?.has_unpublished_changes) || buildFailed;

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          activeModules={activeModules}
          unreadForms={status?.unread_form_submissions ?? 0}
          user={user}
          environment={environment ?? undefined}
          impersonation={impersonation}
          onSignOut={onSignOut}
        />

        <div className="flex flex-1 flex-col overflow-hidden">
          {/* The header lives inside the scroll container (matching the admin
              shell) so it scrolls away with the content and a page's sticky
              tab bar pins at the very top, taking its place. */}
          <main className="flex-1 overflow-y-auto">
            <header className="flex h-14 items-center justify-between gap-4 border-b border-white/10 bg-[var(--color-header)] px-4">
              <Breadcrumbs items={breadcrumbs ?? undefined} showSingle />
              <div className="flex items-center gap-3 ml-auto">
                {status?.build_in_progress ? (
                  <Badge variant="secondary" className="gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Publishing…
                  </Badge>
                ) : canPublish ? (
                  <>
                    {buildFailed ? (
                      <Badge variant="destructive">Build failed</Badge>
                    ) : (
                      <Badge variant="warning">Unpublished changes</Badge>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" disabled={publishing}>
                          {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                          {buildFailed ? "Retry publish" : "Publish"}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Publish your site?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This rebuilds your live site with every saved change — pages,
                            settings and design. It takes about two minutes.
                            {buildFailed && !publishError
                              ? " Your last build failed — try again."
                              : ""}
                            {publishError ? ` (Last attempt failed: ${publishError})` : ""}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={publish}>Publish now</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                ) : null}
              </div>
            </header>

            {/* Past-due banner: portal-wide so the fix-mandate CTA is visible
                on every page, not just the dashboard or billing screen. */}
            {status?.billing_status === "past_due" && (
              <div className="flex items-center gap-3 border-b border-warning/30 bg-warning/15 px-4 py-2.5 text-sm">
                <TriangleAlert className="h-4 w-4 shrink-0 text-warning" />
                <span>
                  Your latest payment didn't go through — please update your Direct Debit
                  to keep your website portal active.
                </span>
                <Link
                  to="/billing"
                  className="ml-auto shrink-0 font-semibold text-primary hover:underline"
                >
                  Fix payment
                </Link>
              </div>
            )}

            {/* Padding lives on the inner wrapper, not the scroll container, so a
                page's sticky sub-header (e.g. the menu tabs) pins flush to the top
                instead of below the padding. Content gutters are p-8; a module tab
                bar breaks back out with matching -mx-8 -mt-8. */}
            <div className="p-8">
              <Outlet context={{ status, statusError, refreshStatus, api, tenantId, activeModules, setBreadcrumbs, userEmail: user?.email ?? "", changePassword }} />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
